import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { 
  Typography, Paper, Grid, Box, Chip, Button, 
  CircularProgress, Alert, Divider, Card, CardContent,
  List, ListItem, ListItemText, ListItemIcon, Avatar
} from '@mui/material';
import { 
  Phone, Email, LocationOn, 
  AttachMoney, VolunteerActivism,
  ArrowBack, Edit, Delete, LinkedIn, Language
} from '@mui/icons-material';
import axios from '../utils/api';

interface Lawyer {
  id: string;
  firstName: string;
  middleInitial?: string;
  lastName: string;
  suffix?: string;
  email: string;
  firmName: string;
  linkedInUrl?: string;
  practiceWebsiteUrl?: string | null;
  yearsPracticingLaw?: number | null;
  lawSchool?: string | null;
  lawSchoolGraduationYear?: number | null;
  comments?: string | null;
  profilePhotoUrl?: string;
  phoneNumbers: Array<{ number: string; type: string }>;
  primaryOffice: {
    line1: string;
    line2?: string;
    city: string;
    state: string;
    zipCode: string;
  };
  otherOffices?: string;
  stateLicenses: Array<{
    state: string;
    barIdNo: string;
    isActive: boolean;
    inGoodStanding: boolean;
  }>;
  practiceAreas: Array<{
    practiceArea?: { name: string }; // Legacy format
    category?: { id: string; name: string };
    subcategory?: { id: string; name: string };
    otherArea?: string;
    subCategoryCustomText?: string;
    percentage?: number | null;
  }>;
  disciplinary: Array<{
    hasAction: boolean;
    narrative?: string;
  }>;
  feeInfo?: {
    hourlyRate?: number;
    otherFees?: string;
  };
  proBonoInterest: boolean;
  offersFreeConsultation?: boolean;
  hasProfessionalLiabilityInsurance?: boolean;
  registrationType?: 'directory' | 'referral';
  createdAt?: string;
}

const LawyerDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const isManagementMode = location.pathname.startsWith('/management');
  const isDirectoryMode = location.pathname.startsWith('/directory');
  
  const [lawyer, setLawyer] = useState<Lawyer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchLawyer = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/lawyers/${id}`);
      setLawyer(response.data);
      setError(null);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to fetch lawyer details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchLawyer();
  }, [fetchLawyer]);

  const handleDelete = async (lawyerId: string) => {
    if (window.confirm('Are you sure you want to delete this lawyer?')) {
      try {
        await axios.delete(`/api/lawyers/${lawyerId}`);
        navigate('/management');
      } catch (err: any) {
        alert('Failed to delete lawyer: ' + (err?.response?.data?.error || 'Unknown error'));
      }
    }
  };

  const getBackPath = () => {
    if (isManagementMode) return '/management';
    if (isDirectoryMode) return '/directory';
    return '/';
  };

  const getTitle = () => {
    if (isManagementMode) return 'Lawyer Management';
    if (isDirectoryMode) return 'Lawyer Directory';
    return 'Lawyer Details';
  };

  const getPracticeAreas = (lawyer: Lawyer) => {
    return lawyer.practiceAreas
      .filter(pa => {
        // Include new format (has category/subcategory), "Other" category (category but no subcategory, has otherArea), or legacy format
        return (pa.category && pa.subcategory) || (pa.category && !pa.subcategory && pa.otherArea) || pa.practiceArea?.name;
      })
      .map(pa => {
        // New format: category:subcategory
        if (pa.category && pa.subcategory) {
          let displayName = `${pa.category.name}: ${pa.subcategory.name}`;
          if (pa.subCategoryCustomText) {
            displayName += ` - ${pa.subCategoryCustomText}`;
          }
          const percentage = pa.percentage;
          return percentage ? `${displayName} (${percentage}%)` : displayName;
        }
        // "Other" category: category but no subcategory, has otherArea
        if (pa.category && !pa.subcategory && pa.otherArea) {
          const displayName = `${pa.category.name}: ${pa.otherArea}`;
          const percentage = pa.percentage;
          return percentage ? `${displayName} (${percentage}%)` : displayName;
        }
        // Legacy format: just practice area name
        if (pa.practiceArea?.name) {
          const name = pa.practiceArea.name;
          const percentage = pa.percentage;
          return percentage ? `${name} (${percentage}%)` : name;
        }
        return null;
      })
      .filter(Boolean);
  };

  const getActiveLicenses = (lawyer: Lawyer) => {
    return lawyer.stateLicenses.filter(license => license.isActive && license.inGoodStanding);
  };

  const getInactiveLicenses = (lawyer: Lawyer) => {
    return lawyer.stateLicenses.filter(license => !license.isActive || !license.inGoodStanding);
  };

  const formatPhoneNumber = (phoneNumber: string): string => {
    // Remove all non-digit characters
    const cleaned = phoneNumber.replace(/\D/g, '');
    
    // If it's a 10-digit number, format it
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    
    // If it's 11 digits and starts with 1, format it
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
    
    // Return original if it doesn't match expected format
    return phoneNumber;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !lawyer) {
    return (
      <Paper sx={{ p: 3 }}>
        <Alert severity="error">{error || 'Lawyer not found'}</Alert>
        <Button 
          variant="contained" 
          onClick={() => navigate(getBackPath())} 
          sx={{ mt: 2 }}
        >
          Back to {getTitle()}
        </Button>
      </Paper>
    );
  }

  const isReferral = lawyer.registrationType === 'referral';

  return (
    <Paper sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3}>
        <Box display="flex" alignItems="center" gap={3}>
          {!isReferral && (
            <Avatar
              src={lawyer.profilePhotoUrl}
              alt={`${lawyer.firstName} ${lawyer.lastName}`}
              sx={{
                width: 120,
                height: 120,
                fontSize: '3rem',
                bgcolor: 'primary.main'
              }}
            >
              {lawyer.firstName.charAt(0)}{lawyer.lastName.charAt(0)}
            </Avatar>
          )}
          <Box>
            <Typography variant="h4" sx={{ mb: 1 }}>
              {lawyer.firstName} {lawyer.middleInitial} {lawyer.lastName} {lawyer.suffix}
            </Typography>
            <Typography variant="h6" color="text.secondary">
              {lawyer.firmName}
            </Typography>
            <Box display="flex" alignItems="center" gap={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
              <Chip
                size="small"
                label={isReferral ? 'Referral Only' : 'Directory'}
                color={isReferral ? 'default' : 'primary'}
                variant={isReferral ? 'outlined' : 'filled'}
              />
              {lawyer.createdAt && (
                <Typography variant="body2" color="text.secondary">
                  Registered on {new Date(lawyer.createdAt).toLocaleDateString()}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
        <Box>
          <Button 
            variant="outlined" 
            startIcon={<ArrowBack />}
            onClick={() => navigate(getBackPath())}
            sx={{ mr: 1 }}
          >
            Back to {getTitle()}
          </Button>
          {isManagementMode && (
            <>
              <Button 
                variant="contained" 
                startIcon={<Edit />}
                onClick={() => navigate(`/management/edit/${lawyer.id}`)}
                sx={{ mr: 1 }}
              >
                Edit
              </Button>
              <Button 
                variant="contained" 
                color="error" 
                startIcon={<Delete />}
                onClick={() => handleDelete(lawyer.id)}
              >
                Delete
              </Button>
            </>
          )}
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Basic Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Basic Information
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon><Email /></ListItemIcon>
                  <ListItemText primary="Email" secondary={lawyer.email} />
                </ListItem>
                {!isReferral && (
                  <>
                    <ListItem>
                      <ListItemIcon><Language /></ListItemIcon>
                      <ListItemText
                        primary="Business website"
                        secondary={
                          lawyer.practiceWebsiteUrl ? (
                            <Box
                              component="a"
                              href={'https://' + lawyer.practiceWebsiteUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              sx={{
                                color: 'primary.main',
                                textDecoration: 'none',
                                '&:hover': { textDecoration: 'underline' },
                              }}
                            >
                              {lawyer.practiceWebsiteUrl}
                            </Box>
                          ) : (
                            'Not provided'
                          )
                        }
                      />
                    </ListItem>
                    {lawyer.linkedInUrl && (
                      <ListItem>
                        <ListItemIcon><LinkedIn /></ListItemIcon>
                        <ListItemText
                          primary="LinkedIn"
                          secondary={
                            <Box component="a"
                                 href={'https://' + lawyer.linkedInUrl}
                                 target="_blank"
                                 rel="noopener noreferrer"
                                 sx={{
                                   color: 'primary.main',
                                   textDecoration: 'none',
                                   '&:hover': { textDecoration: 'underline' }
                                 }}
                            >
                              {lawyer.linkedInUrl}
                            </Box>
                          }
                        />
                      </ListItem>
                    )}
                    <ListItem>
                      <ListItemText
                        primary="Comments"
                        secondary={
                          lawyer.comments?.trim()
                            ? (
                              <Typography component="span" variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                {lawyer.comments}
                              </Typography>
                            )
                            : 'Not provided'
                        }
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Years Practicing Law"
                        secondary={
                          lawyer.yearsPracticingLaw !== null && lawyer.yearsPracticingLaw !== undefined
                            ? `${lawyer.yearsPracticingLaw}`
                            : 'Not provided'
                        }
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Law School"
                        secondary={lawyer.lawSchool || 'Not provided'}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemText
                        primary="Law School Graduation Year"
                        secondary={
                          lawyer.lawSchoolGraduationYear !== null && lawyer.lawSchoolGraduationYear !== undefined
                            ? `${lawyer.lawSchoolGraduationYear}`
                            : 'Not provided'
                        }
                      />
                    </ListItem>
                  </>
                )}
                {lawyer.phoneNumbers.map((phone, idx) => (
                  <ListItem key={idx}>
                    <ListItemIcon><Phone /></ListItemIcon>
                    <ListItemText 
                      primary={`${phone.type.charAt(0).toUpperCase() + phone.type.slice(1)} Phone`}
                      secondary={formatPhoneNumber(phone.number)} 
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Office Location */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Primary Office
              </Typography>
              <Box display="flex" alignItems="center" mb={1}>
                <LocationOn sx={{ mr: 1 }} />
                <Typography variant="body2">
                  {lawyer.primaryOffice.line1}
                </Typography>
              </Box>
              {lawyer.primaryOffice.line2 && (
                <Typography variant="body2" sx={{ ml: 3 }}>
                  {lawyer.primaryOffice.line2}
                </Typography>
              )}
              <Typography variant="body2" sx={{ ml: 3 }}>
                {lawyer.primaryOffice.city}, {lawyer.primaryOffice.state} {lawyer.primaryOffice.zipCode}
              </Typography>
              {lawyer.otherOffices && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" gutterBottom>
                    Other Offices:
                  </Typography>
                  <Typography variant="body2">
                    {lawyer.otherOffices}
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Practice Areas */}
        {!isReferral && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Practice Areas
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {getPracticeAreas(lawyer).map((area, idx) => (
                    <Chip key={idx} label={area} color="primary" variant="outlined" />
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* State Licenses */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                State Licenses
              </Typography>
              {isReferral ? (
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {lawyer.stateLicenses.map((license, idx) => (
                    <Chip
                      key={idx}
                      label={`${license.state} (${license.barIdNo})`}
                      size="small"
                    />
                  ))}
                </Box>
              ) : (
                <>
                  {getActiveLicenses(lawyer).length > 0 && (
                    <>
                      <Typography variant="subtitle2" color="success.main" gutterBottom>
                        Active & In Good Standing:
                      </Typography>
                      <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                        {getActiveLicenses(lawyer).map((license, idx) => (
                          <Chip
                            key={idx}
                            label={`${license.state} (${license.barIdNo})`}
                            color="success"
                            size="small"
                          />
                        ))}
                      </Box>
                    </>
                  )}
                  {getInactiveLicenses(lawyer).length > 0 && (
                    <>
                      <Typography variant="subtitle2" color="warning.main" gutterBottom>
                        Inactive or Not in Good Standing:
                      </Typography>
                      <Box display="flex" flexWrap="wrap" gap={1}>
                        {getInactiveLicenses(lawyer).map((license, idx) => (
                          <Chip
                            key={idx}
                            label={`${license.state} (${license.barIdNo})`}
                            color="warning"
                            size="small"
                          />
                        ))}
                      </Box>
                    </>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Disciplinary Actions */}
        {!isReferral && lawyer.disciplinary.some(d => d.hasAction) && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Disciplinary Actions
                </Typography>
                {lawyer.disciplinary.map((disciplinary, idx) => (
                  disciplinary.hasAction && (
                    <Typography key={idx} variant="body2" color="error">
                      {disciplinary.narrative}
                    </Typography>
                  )
                ))}
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Fee Information */}
        {!isReferral && lawyer.feeInfo && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Fee Information
                </Typography>
                <List dense>
                  {lawyer.feeInfo.hourlyRate && (
                    <ListItem>
                      <ListItemIcon><AttachMoney /></ListItemIcon>
                      <ListItemText 
                        primary="Standard Hourly Rate" 
                        secondary={`$${lawyer.feeInfo.hourlyRate}/hour`} 
                      />
                    </ListItem>
                  )}
                  {lawyer.feeInfo.otherFees && (
                    <ListItem>
                      <ListItemIcon><AttachMoney /></ListItemIcon>
                      <ListItemText 
                        primary="Other Fee Options" 
                        secondary={lawyer.feeInfo.otherFees} 
                      />
                    </ListItem>
                  )}
                </List>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Pro Bono Interest */}
        {!isReferral && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Pro Bono Interest
                </Typography>
                <Box display="flex" alignItems="center">
                  <VolunteerActivism sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    {lawyer.proBonoInterest
                      ? 'Interested in pro bono opportunities'
                      : 'Not interested in pro bono opportunities'
                    }
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Free Initial Consultation */}
        {!isReferral && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Free Initial Consultation
                </Typography>
                <Typography variant="body2">
                  Offers free 30-minute consultation:{' '}
                  {lawyer.offersFreeConsultation ? 'Yes' : 'No'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Professional Liability Insurance */}
        {!isReferral && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Professional Liability Insurance
                </Typography>
                <Typography variant="body2">
                  Maintains professional liability insurance:{' '}
                  {lawyer.hasProfessionalLiabilityInsurance ? 'Yes' : 'No'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

export default LawyerDetail; 